/// <reference types="vite/client" />

import mixpanel from 'mixpanel-browser';

import { ANALYTICS_EVENTS, type AnalyticsEventName, NEW_EXPERIENCE_ANALYTICS } from './constants';

type UserContext = {
  userId?: string;
  orgId?: string;
  role?: string;
  userCreatedAt?: Date | string;
};

const getToken = () => import.meta.env.VITE_MIXPANEL_TOKEN as string | undefined;

const getUserAnalyticsProperties = ({ userId, orgId, role, userCreatedAt }: UserContext) => ({
  userId,
  orgId,
  role,
  userCreatedAt: userCreatedAt instanceof Date ? userCreatedAt.toISOString() : userCreatedAt,
});

export const initAnalytics = () => {
  const token = getToken();
  if (!token) return;
  mixpanel.init(token, {
    debug: true,
    record_sessions_percent: 1,
    record_heatmap_data: true,
  });
};

export const setUserContext = ({ userId, orgId, role, userCreatedAt }: UserContext) => {
  if (!getToken()) return;
  const userProperties = getUserAnalyticsProperties({ userId, orgId, role, userCreatedAt });
  if (userId) mixpanel.identify(userId);
  mixpanel.register(userProperties);
  if (!userId) return;
  mixpanel.people.set(userProperties);
  mixpanel.people.set_once({ firstSeenAt: new Date().toISOString() });
};

/** Adds the active experience to every subsequent event and the current user's Mixpanel profile. */
export const setNewExperienceAnalyticsContext = (isOptedIntoNewExperience: boolean) => {
  if (!getToken()) return;
  const properties = {
    newExperience: isOptedIntoNewExperience
      ? NEW_EXPERIENCE_ANALYTICS.modes.new
      : NEW_EXPERIENCE_ANALYTICS.modes.original,
  };
  mixpanel.register(properties);
  mixpanel.people.set(properties);
};

export const trackEvent = (eventName: AnalyticsEventName, props?: Record<string, any>) => {
  if (!getToken()) return;
  mixpanel.track(eventName, props);
};

/** Reports only the error class and source so analytics never receives error text or stack traces. */
export const trackError = (source: string, error: unknown) => {
  trackEvent(ANALYTICS_EVENTS.clientError, {
    source,
    errorName: error instanceof Error ? error.name : typeof error,
  });
};
